import { DateTime, Interval } from 'luxon';

type DateInput = string | DateTime | null | undefined;

function parseToJsDate(date: string | DateTime): Date {
  if (typeof date === 'string') {
    return new Date(date);
  }

  return date.toJSDate();
}

/**
 * Simple date formatter function to handle formatting dates
 */
export const formatDate = (dateString: DateInput): string | undefined => {
  if (!dateString) {
    return undefined;
  }

  const date = parseToJsDate(dateString);
  return new Intl.DateTimeFormat('en-US', { year: 'numeric', month: 'short', day: 'numeric' }).format(date);
};

/**
 * Difference in days between two dates
 */
export const differenceInDays = (
  date1: DateInput,
  date2: DateInput,
): { time: number; unit: 'days' | 'day' } | undefined => {
  if (!date1 || !date2) {
    return undefined;
  }

  const interval = Interval.fromDateTimes(parseToJsDate(date2), parseToJsDate(date1));

  if (interval.length('days') < 1) {
    return { time: 0, unit: 'day' };
  }

  return { time: parseInt(interval.length('days').toFixed(0), 10), unit: 'days' };
};

/**
 * Normalizes various date inputs to Luxon DateTime | null for use with MUI DatePicker (AdapterLuxon).
 * The picker expects a DateTime with a valid .zone; passing strings, invalid DateTimes, or plain
 * objects causes "value.zone is undefined" errors. A bare `YYYY-MM-DD` (a `LocalDate` such as a date
 * of birth or a passport expiry) parses to local midnight of that exact day, so the picker shows the
 * stored day whatever the viewer's zone.
 */
export const parseDateTimeForPicker = (date: string | DateTime | null | undefined): DateTime | null => {
  if (date == null) {
    return null;
  }
  if (typeof date === 'string') {
    const dt = DateTime.fromISO(date);
    return dt.isValid && dt.zone ? dt : null;
  }
  if (date instanceof DateTime && date.isValid && date.zone) {
    return date;
  }
  return null;
};

/**
 * Serialises a date picker value to the `LocalDate` wire format (`YYYY-MM-DD`). A date of birth or a
 * passport expiry is a calendar day, not an instant: sending the picker's local midnight with its offset
 * let the stored day depend on the browser's zone, so the airline could see the day before.
 */
export const toLocalDate = (date: DateTime | null | undefined): string | undefined => {
  if (!date?.isValid) {
    return undefined;
  }

  return date.toISODate() ?? undefined;
};

/**
 * Luxon format string for a word-month date whose day/month order follows the viewer's locale, so
 * the same value reads as US "October 25, 2025" or AU "25 October 2025". Because the month is always
 * spelled out, neither ordering can be misread the way a numeric M/D vs D/M date can — the whole
 * point for passport/travel-document dates. The order is derived from `Intl` (the browser's locale)
 * rather than hardcoded, so any locale gets its natural ordering, not just en-US/en-AU.
 */
export const wordMonthDateFormat = (locale?: string): string => {
  // 2nd of January: the day (2) and month (1) values differ, so the order of the rendered parts is
  // unambiguous regardless of locale; we only inspect their order, never their values.
  const reference = new Date(Date.UTC(2025, 0, 2));
  const parts = new Intl.DateTimeFormat(locale, {
    day: 'numeric',
    month: 'long',
    year: 'numeric',
    timeZone: 'UTC',
  }).formatToParts(reference);
  const monthIndex = parts.findIndex((part) => part.type === 'month');
  const dayIndex = parts.findIndex((part) => part.type === 'day');
  return monthIndex < dayIndex ? 'MMMM d, yyyy' : 'd MMMM yyyy';
};

/**
 * Render a backend-resolved trip date (`Trip.startDate` / `Trip.endDate`, a bare `YYYY-MM-DD`).
 *
 * Trip dates must never be derived from `startAt`/`endAt` in the client: those are instants, so
 * formatting them in the viewer's zone shows a different day depending on where the viewer sits — a
 * 06:00 Sydney departure reads as the previous day from anywhere west of it. The backend resolves the
 * traveler's wall-clock day once; this only formats it, keeping the zone the string carries so no
 * local conversion can shift it.
 */
export const formatTripDate = (date: string | null | undefined, format = 'dd LLL yyyy'): string | undefined => {
  if (!date) {
    return undefined;
  }

  const dateTime = DateTime.fromISO(date, { setZone: true });
  return dateTime.isValid ? dateTime.toFormat(format) : undefined;
};

/**
 * Formats a date using Luxon's toFormat method.
 * Handles both string (ISO) and DateTime inputs, converting strings to DateTime as needed.
 */
export const formatDateWithLuxon = (date: DateInput, format: string): string | undefined => {
  if (!date) {
    return undefined;
  }

  const dateTime = typeof date === 'string' ? DateTime.fromISO(date) : date;

  if (!dateTime.isValid) {
    return undefined;
  }

  return dateTime.toFormat(format);
};
